import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import { Skeleton, Tooltip, Typography } from '@mui/material';

import { ChartDisplayLayout } from './components/ChartDisplayLayout';
import { ChartViewPanel } from './components/ChartViewPanel';
import { ChartViewPanelOptionsValue } from 'types/api/Tenant/AROChart/ChartViewTypes';
import { NAVIGATION_COMPONENT_TYPE } from 'constant/NavigationConstant';
import { ControllerNavigationDirection } from 'pages/Tenant/AroDashboard/ControllerNavigation';
import {
  generateBreadCumbItemsV2,
  TenantHomeDashboardNavigationBar,
} from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { useGetBreadcumbItems } from 'services/v1/Common/NavBarMenuService';
import { useGetChartViewLayout } from 'services/v1/Tenant/AROChart/ChartViewService';

// ── 4B: Loading skeleton ──
function PageLoadingSkeleton() {
  return (
    <Stack gap={1.5} height='100%' p={2}>
      {/* Panel skeleton */}
      <Stack
        direction='row'
        gap={2}
        sx={{ bgcolor: '#fff', py: 1.5, px: 2, borderRadius: 1, border: '1px solid #eef0f4' }}
      >
        <Skeleton variant='rounded' width={220} height={40} />
        <Skeleton variant='rounded' width={200} height={40} />
        <Skeleton variant='rounded' width={160} height={40} />
        <Skeleton variant='rounded' width={220} height={40} />
        <Stack flex={1} />
        <Skeleton variant='rounded' width={100} height={40} />
      </Stack>
      {/* Grid skeleton */}
      <Stack
        flex={1}
        sx={{ bgcolor: '#f8f9fb', borderRadius: 2, border: '1px solid #eef0f4', p: 2 }}
      >
        <Stack direction='row' gap={1.5} height='50%' mb={1.5}>
          <Skeleton variant='rounded' animation='wave' sx={{ flex: 1, borderRadius: 2 }} />
          <Skeleton variant='rounded' animation='wave' sx={{ flex: 1, borderRadius: 2 }} />
        </Stack>
        <Stack direction='row' gap={1.5} height='50%'>
          <Skeleton variant='rounded' animation='wave' sx={{ flex: 1, borderRadius: 2 }} />
          <Skeleton variant='rounded' animation='wave' sx={{ flex: 1, borderRadius: 2 }} />
        </Stack>
      </Stack>
    </Stack>
  );
}

// ── 4C: Keyboard shortcut indicator ──
const SHORTCUT_MAP: { key: string; direction: ControllerNavigationDirection; label: string }[] = [
  { key: 'ArrowUp', direction: ControllerNavigationDirection.UP, label: 'Drill up' },
  { key: 'ArrowDown', direction: ControllerNavigationDirection.DOWN, label: 'Drill down' },
  { key: 'ArrowLeft', direction: ControllerNavigationDirection.LEFT, label: 'Previous view' },
  { key: 'ArrowRight', direction: ControllerNavigationDirection.RIGHT, label: 'Next view' },
];

function KeyboardShortcutHint() {
  return (
    <Tooltip
      arrow
      title={
        <Stack gap={0.5} py={0.5}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>Keyboard Shortcuts</Typography>
          {SHORTCUT_MAP.map((s) => (
            <Stack key={s.key} direction='row' alignItems='center' gap={1}>
              <Typography
                component='span'
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  minWidth: 20,
                  textAlign: 'center',
                }}
              >
                {s.key.replace('Arrow', '')}
              </Typography>
              <Typography sx={{ fontSize: 10 }}>{s.label}</Typography>
            </Stack>
          ))}
          <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
            Only active when no input is focused
          </Typography>
        </Stack>
      }
    >
      <Stack
        direction='row'
        alignItems='center'
        gap={0.5}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          bgcolor: '#333',
          color: '#fff',
          px: 1.25,
          py: 0.5,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 600,
          opacity: 0.6,
          cursor: 'default',
          zIndex: 10,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 1 },
          userSelect: 'none',
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#fff' }}>?</Typography>
        <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>Shortcuts</Typography>
      </Stack>
    </Tooltip>
  );
}

export default function AROChartViewPage() {
  const { chartViewId } = useParams<{ chartViewId: string }>();
  const panelRef = useRef<{
    navigate: (direction: ControllerNavigationDirection) => void;
    enabledNavigations: ControllerNavigationDirection[];
    isLoadingOptionsData: boolean;
  }>(null);

  const [optionValue, setOptionValue] = useState<ChartViewPanelOptionsValue>({
    members: null,
    accumulation: null,
    measure: null,
    insightAttribute: null,
  });

  const { data: breadCrumbItems } = useGetBreadcumbItems(chartViewId, NAVIGATION_COMPONENT_TYPE.CHART);

  const breadCrumbs = useMemo(() => {
    return generateBreadCumbItemsV2(breadCrumbItems?.data || []);
  }, [breadCrumbItems?.data]);

  const { data: chartViewData, isLoading: isLoadingChartViewData } = useGetChartViewLayout({
    chartViewId,
  });

  const chartsLayout = useMemo(() => {
    if (chartViewData?.data) {
      return {
        values: chartViewData?.data?.charts,
        layouts: JSON.parse(chartViewData?.data?.layout),
      };
    }
    return {
      values: [],
      layouts: { lg: [] },
    };
  }, [chartViewData?.data]);

  const memberIds: string[] = useMemo(() => {
    if (optionValue.members) {
      return optionValue.members.map((item) => item.value.toString());
    }
    return [];
  }, [optionValue.members]);

  const handleOnOptionChange = (value: ChartViewPanelOptionsValue) => {
    setOptionValue(value);
  };

  // ── 4C: Keyboard shortcut handler ──
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Skip if user is typing in an input/select
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;

      const match = SHORTCUT_MAP.find((s) => s.key === e.key);
      if (!match) return;

      const ref = panelRef.current;
      if (!ref) return;

      // Only trigger if this navigation direction is enabled
      if (ref.enabledNavigations?.includes(match.direction)) {
        e.preventDefault();
        ref.navigate(match.direction);
      }
    },
    []
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <TenantHomeDashboardNavigationBar
      containerSx={{ height: '100%', flex: 1, flexDirection: 'column', overflowY: 'auto', display: 'flex' }}
      rootContainerSx={{ height: '95vh' }}
      breadCrumbs={breadCrumbs}
    >
      {/* ── 4B: Show skeleton while layout is loading ── */}
      {isLoadingChartViewData && <PageLoadingSkeleton />}

      {!isLoadingChartViewData && (
        <>
          <ChartViewPanel ref={panelRef} onOptionChange={handleOnOptionChange} />

          <ChartDisplayLayout
            layouts={chartsLayout?.layouts}
            values={chartsLayout?.values}
            generalFilter={{
              memberIds,
              cubeAttributeId: optionValue.measure?.value?.toString() ?? '',
              accumulationType: optionValue.accumulation?.value?.toString() ?? '',
              insightViewAttributeIds: optionValue.insightAttribute?.map((item) => item.value.toString()) ?? [],
            }}
            isLoading={isLoadingChartViewData}
          />

          {/* ── 4C: Keyboard shortcut hint ── */}
          <KeyboardShortcutHint />
        </>
      )}
    </TenantHomeDashboardNavigationBar>
  );
}
